import getTablero from './tablero_parameters';
import getPositions from './tablero_positions';

/**
 * Inicializa el Tablero para el tema de tarot.
 *
 * Esta función configura las dimensiones y posiciones de las cartas en el tablero,
 * ajusta la altura del tablero y las imágenes de las cartas, y aplica animaciones
 * CSS a las cartas según la configuración del spread del tema de tarot.
 *
 * @module initializeTablero
 *
 * @global
 * @function
 *
 * @returns {void}
 */
/**
 * Inicializa el Tablero para una instancia concreta.
 * @param {Element} [root] Contenedor raíz para scopear las consultas DOM. Si no se pasa, usa document.
 */
export default function initializeTablero(root) {
  ///////////////////////////////  VARIABLES  ///////////////////////////////
  // #region Variables
  ///////////////////////////////////////////////////////////////////////////

 

  const scope = (root && typeof root.querySelector === 'function') ? root : document;
  const tarot_theme = tarokina.tarot_theme;
  const tablero = scope.querySelector('.tablero');
  const imgBorde = scope.querySelectorAll('.imgBorde');
  const cards = scope.querySelectorAll('.picture_tableroImgs');
  const firstCardTablero = scope.querySelector('.tableroImgs');
  // Detección de soporte para variables CSS; si no hay soporte, aplicaremos transformaciones inline
  const supportsCSSVars = !!(typeof window !== 'undefined' && window.CSS && typeof window.CSS.supports === 'function' && (window.CSS.supports('(--a: 0)') || window.CSS.supports('color', 'var(--fake)')));
  // Detección de RTL del documento
  const isRTL = (function(){
    try {
      const d = document;
      if (!d) return false;
      const bodyDir = (window.getComputedStyle && d.body) ? window.getComputedStyle(d.body).direction : '';
      return (
        (d.dir && String(d.dir).toLowerCase() === 'rtl') ||
        (d.documentElement && d.documentElement.dir && String(d.documentElement.dir).toLowerCase() === 'rtl') ||
        (String(bodyDir).toLowerCase() === 'rtl')
      );
    } catch (e) { return false; }
  })();
  let spread = tarot_theme["tkta_spread"];
  let spreadN = tarokina.spread_N

  let cardWidth, cardHeight, tableroWidth, tableroHeight, moveCruz, padding, y_tablero, x_tablero, tableroMedidas, tableroMedidas_y;

 

  // MODULE Importar Parámetros del Tablero
  // IMPORTANTE: respeta la firma de getTablero(spread, cards, imgBorde, tarot_theme, root)
  const tableroParameters = getTablero(spread, cards, imgBorde, tarot_theme, scope);
  cardWidth = tableroParameters.ImgWidth;
  y_tablero = tableroParameters.y_tablero;
  x_tablero = tableroParameters.x_tablero;
  padding = tableroParameters.padding;
  moveCruz = tableroParameters.moveCruz;

  // Calcular la altura de la carta a partir del ancho y el aspect ratio natural (estable frente a transforms)
  // Fallback a un ratio típico de cartas de tarot si la imagen aún no está cargada
  {
    let ratio = 1.58; // fallback aproximado alto/ancho
    if (firstCardTablero && firstCardTablero.naturalWidth > 0) {
      ratio = firstCardTablero.naturalHeight / firstCardTablero.naturalWidth;
    }
    cardHeight = Math.round(cardWidth * ratio);
  }


  if (tablero) {
    // 1) Establecer una altura provisional segura basada en el tamaño de carta y padding
    const provisionalHeight = Math.max(1, Math.ceil(cardHeight * 2 + (padding ? Math.max(0, Math.floor(padding)) : 0)));
    tablero.style.height = provisionalHeight + 'px';
    // 2) Medir el tablero provisionalmente
    let rectTablero = tablero.getBoundingClientRect();
    tableroWidth = rectTablero.width - cardWidth;
    tableroHeight = rectTablero.height - cardHeight;
  }

  // Helper: fallback sin Proxy → genera rejilla centrada
  function buildFallbackPositions(count) {
    count = (typeof count === 'number' && count > 0) ? count : cards.length || 0;
    if (count <= 0) return [];
    function getGrid(n) {
      switch (n) {
        case 1: return { cols: 1, rows: 1 };
        case 2: return { cols: 2, rows: 1 };
        case 3: return { cols: 3, rows: 1 };
        case 4: return { cols: 2, rows: 2 };
        case 5: return { cols: 3, rows: 2 };
        case 6: return { cols: 3, rows: 2 };
        case 7: return { cols: 4, rows: 2 };
        case 8: return { cols: 4, rows: 2 };
        case 9: return { cols: 3, rows: 3 };
        case 10: return { cols: 5, rows: 2 };
        default:
          var cols = Math.ceil(Math.sqrt(n));
          var rows = Math.ceil(n / cols);
          return { cols: cols, rows: rows };
      }
    }
    var grid = getGrid(count);
    var cols = grid.cols, rows = grid.rows;
    var gridW = cols * cardWidth + (cols - 1) * padding;
    var gridH = rows * cardHeight + (rows - 1) * padding;
    var startX = Math.max(0, (tableroWidth - gridW) / 2) - (x_tablero || 0);
    var startY = Math.max(0, (tableroHeight - gridH) / 2) + (y_tablero || 0);
    var list = [];
    for (var i = 0; i < count; i++) {
      var r = Math.floor(i / cols);
      var c = i % cols;
      var x = startX + c * (cardWidth + padding);
      var y = startY + r * (cardHeight + padding);
      list.push({ x: Math.round(x), y: Math.round(y), rotate: 0, zIndex: i + 1 });
    }
    return list;
  }

  // 3) Calcular posiciones con la altura provisional (con fallback sin Proxy)
  let positionsCards;
  try {
    positionsCards = getPositions(tableroWidth, tableroHeight, moveCruz, padding, cardHeight, cardWidth, y_tablero, x_tablero);
  } catch (e) {
    positionsCards = {};
    positionsCards[spread] = buildFallbackPositions(cards.length);
  }

  // 4) Calcular altura requerida en función de la altura de cartas y offsets del spread
  //    Objetivo: requiredHeight = 2 * max(|deltaY|) + cardHeight (+ pequeño margen)
  (function computeDynamicHeight() {
    if (!tablero || !positionsCards) return;
    const pList = positionsCards && positionsCards[spread] ? positionsCards[spread] : null;
    if (!pList || !pList.length) return;
    // Altura basada en extremos: desde la carta más alta hasta la más baja
    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < pList.length; i++) {
      const py = (pList[i] && typeof pList[i].y === 'number') ? pList[i].y : 0;
      if (py < minY) minY = py;
      if (py > maxY) maxY = py;
    }
    // Margen extra mínimo (usa padding como referencia)
    const extraMargin = Math.max(0, Math.floor(padding));
    // Rango de posiciones + altura de carta: cubre de top de la superior a bottom de la inferior
  const requiredHeight = Math.ceil((maxY - minY) + cardHeight + extraMargin);
  const currentHeight = parseFloat((tablero && tablero.style && tablero.style.height) ? tablero.style.height : '0') || 0;
    // Límite máximo relativo al viewport para evitar tableros excesivamente altos
    const vpH = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 0;
    const maxHeight = vpH > 0 ? Math.floor(vpH * 1.8) : requiredHeight;
    const finalHeight = Math.min(requiredHeight, maxHeight);

    if (isFinite(finalHeight) && finalHeight > 0 && Math.abs(finalHeight - currentHeight) > 1) {
      // 5) Fijar altura dinámica (con clamp) y activar scroll si se ha limitado
      if (finalHeight < requiredHeight) {
        tablero.style.overflowY = 'auto';
      } else {
        tablero.style.overflowY = 'visible';
      }
      tablero.style.height = finalHeight + 'px';
      const rect = tablero.getBoundingClientRect();
      tableroWidth = rect.width - cardWidth;
      tableroHeight = rect.height - cardHeight;
      // 6) Recalcular posiciones con la altura definitiva (con fallback)
      try {
        positionsCards = getPositions(tableroWidth, tableroHeight, moveCruz, padding, cardHeight, cardWidth, y_tablero, x_tablero);
      } catch (e2) {
        positionsCards = {};
        positionsCards[spread] = buildFallbackPositions(cards.length);
      }
    }
  })();

  // En RTL, reflejar posiciones horizontalmente e invertir rotaciones
  if (isRTL && positionsCards && positionsCards[spread] && Array.isArray(positionsCards[spread])) {
    const list = positionsCards[spread];
    for (let i = 0; i < list.length; i++) {
      const p = list[i] || {};
      const px = (typeof p.x === 'number') ? p.x : 0;
      p.x = Math.round(tableroWidth - px);
      if (typeof p.rotate === 'number') {
        p.rotate = -p.rotate;
      }
      list[i] = p;
    }
  }

  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    if (!card) continue;

    // Fijar tamaño estable del contenedor de la carta
    card.style.width = cardWidth + 'px';
    card.style.height = cardHeight + 'px';

    // Asegurar que la imagen ocupa el contenedor sin depender del layout previo
    const img = card.querySelector('.tableroImgs');
    if (img) {
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'cover';
      img.style.display = 'block';
    }

    // Ajusta el tamaño del borde para esta carta de forma determinista (independiente de transforms)
    if (imgBorde && imgBorde[i]) {
      imgBorde[i].style.width = cardWidth + 'px';
      imgBorde[i].style.height = cardHeight + 'px';
    }

    // Fallbacks seguros para posiciones
    const pList = positionsCards && positionsCards[spread] ? positionsCards[spread] : null;
    const p = pList && pList[i] ? pList[i] : { x: 0, y: 0, rotate: 0, zIndex: i + 1 };

    // Aplica las transformaciones (fallback sin variables CSS)
    if (supportsCSSVars) {
      card.style.setProperty('--translate-x', p.x + 'px');
      card.style.setProperty('--translate-y', p.y + 'px');
      card.style.setProperty('--rotate', ((p.rotate !== undefined ? -p.rotate : 0)) + 'deg');
    } else {
      const rot = (p.rotate !== undefined ? -p.rotate : 0);
      const t = 'translate(' + p.x + 'px,' + p.y + 'px) rotate(' + rot + 'deg)';
      card.style.transform = t;
      card.style.webkitTransform = t;
      // Evitar animaciones dependientes de variables
      card.style.transition = 'none';
      // Asegurar visibilidad si las reglas CSS dependen de variables para mostrar
      card.style.opacity = '1';
    }
    card.style.zIndex = String(p.zIndex != null ? p.zIndex : (i + 1));
  }


  // Posiciones desde Tablero Positions solo para las Guías
  let positionsBorde;
  try {
    positionsBorde = getPositions(tableroWidth, tableroHeight, moveCruz, padding, cardHeight, cardWidth, y_tablero, x_tablero);
  } catch (e) {
    positionsBorde = {};
    positionsBorde[spread] = buildFallbackPositions(imgBorde.length);
  }
  if (isRTL && positionsBorde && positionsBorde[spread] && Array.isArray(positionsBorde[spread])) {
    const listB = positionsBorde[spread];
    for (let i = 0; i < listB.length; i++) {
      const p = listB[i] || {};
      const px = (typeof p.x === 'number') ? p.x : 0;
      p.x = Math.round(tableroWidth - px);
      if (typeof p.rotate === 'number') {
        p.rotate = -p.rotate;
      }
      listB[i] = p;
    }
  }
  for (let i = 0; i < imgBorde.length; i++) {
    const borde = imgBorde[i];
    if (!borde) continue;
    const pList = positionsBorde && positionsBorde[spread] ? positionsBorde[spread] : null;
    const p = pList && pList[i] ? pList[i] : { x: 0, y: 0, rotate: 0 };
    // Aplica las transformaciones (fallback sin variables CSS)
    if (supportsCSSVars) {
      borde.style.setProperty('--translate-x', p.x + 'px');
      borde.style.setProperty('--translate-y', p.y + 'px');
      borde.style.setProperty('--rotate', ((p.rotate !== undefined ? -p.rotate : 0)) + 'deg');
      borde.classList.add('animated');
    } else {
      const rot = (p.rotate !== undefined ? -p.rotate : 0);
      const t = 'translate(' + p.x + 'px,' + p.y + 'px) rotate(' + rot + 'deg)';
      borde.style.transform = t;
      borde.style.webkitTransform = t;
      // Evitar animaciones dependientes de variables
      borde.style.transition = 'none';
      // Asegurar visibilidad si las reglas CSS dependen de variables para mostrar
      borde.style.opacity = '1';
    }
  }

}